<template>
  <t-card :class="['matching-slot', stateClass]" data-qe-id="matching-slot">
    <div v-if="name">
      <div class="avatar-wrapper">
        <div class="avatar-image">
          <img :src="userAvatarUrl + 'PROFILE_185.png'" />
        </div>
      </div>
      <p class="name-label">{{ name }}</p>
      <p class="state-label" data-qe-id="matching-state">
        <span>{{ $t('matchingView.searchState.found') }}</span>
      </p>
    </div>

    <div v-else>
      <div class="avatar-wrapper small" :class="stateClass">
        <div class="avatar-image">
          <c-generic-avatar :state="state" />
        </div>
      </div>
      <p class="state-label" data-qe-id="matching-state">
        <span class="progressinfo-pending">{{ $t('matchingView.searchState.searching') }}</span>
        <span class="progressinfo-finished"> {{ $t('matchingView.searchState.found') }}</span>
      </p>
      <transition name="fade">
        <i v-if="state === MatchingProgress.SUCCESS" class="tick icf-ok" />
      </transition>
    </div>
  </t-card>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { MatchingProgress } from '@/model/Matching'
import CGenericAvatar from '@/components/CGenericAvatar.vue'

const props = withDefaults(defineProps<{ userAvatarUrl?: string; name?: string; state: MatchingProgress }>(), {
  state: MatchingProgress.LOADING,
})

const stateClass = computed(() => {
  switch (props.state) {
    case MatchingProgress.LOADING:
      return 'loading'
    case MatchingProgress.SUCCESS:
      return 'success'
    case MatchingProgress.FAILED:
      return 'failed'
    default:
      return ''
  }
})
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="less" scoped>
@import '~@npm/@gd/common-styles/dist/card';
@import '~@npm/@gd/common-styles/dist/color-vars';
@import '../styles/vars';

.matching-slot {
  display: inline-block;
  margin: 8em 1.5625rem 2em 1.5625rem;
  border-radius: 6px;
  padding: 0 1.25rem;
  text-align: center;

  .name-label {
    margin-bottom: 0.875rem;
    font-size: 1.25rem;
    font-weight: 700;
  }

  .state-label {
    font-size: 1em;
    margin-bottom: 1.25em;
    font-weight: 700;
    color: @c0-e;
  }

  .avatar-wrapper {
    position: relative;
    margin-top: -5.5em;
    margin-bottom: 1.25rem;
    width: 10.5em;
    height: 10.5em;
    border-radius: 100%;
    background: @c0-b;
    box-shadow: 0 0.3em 0.4em rgba(0, 0, 0, 0.2);
    transform: scale(1) translateZ(0);
    overflow: hidden;
    transition: all 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    &.small {
      width: 7em;
      height: 7em;
      margin-top: -3.5em;
      margin-bottom: 1.25rem;

      .avatar-image {
        width: 5.5rem;
        height: 5.5rem;
        top: 0.9em;
        left: 1em;
        background: white;
        svg {
          width: 5.5rem;
          height: 5.5rem;
          top: 0.9em;
          left: 1em;
        }
      }
    }

    .avatar-image {
      position: absolute;
      display: block;
      width: 8.5em;
      height: 8.5em;
      left: 1.3em;
      top: 1.2em;
      border-radius: 100%;
      overflow: hidden;
      -webkit-transform: translateZ(0);
      transform: translateZ(0);
      position: relative;
      img {
        width: 100%;
      }
      &:after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border-radius: 100%;
        box-shadow: 0 0 0.25em rgba(0, 0, 0, 0.2) inset;
      }
    }
  }

  .tick {
    position: absolute;
    bottom: -1.75em;
    color: @c9-g;
    font-size: 1.5rem;
    transform: translateX(-50%);
    left: 50%;
  }

  .progressinfo-pending {
    display: initial;
  }
  .progressinfo-finished {
    display: none;
  }

  .colorScheme.red & {
    .card(@colorScheme--red);
    .avatar-wrapper {
      background: @colorScheme--red;
      &.loading {
        background: @c0-b;
      }
    }
  }
  .colorScheme.midGreen & {
    .card(@colorScheme--midGreen);
    &.loading {
      .card(@c0-b);
    }
    .avatar-wrapper {
      background: @colorScheme--midGreen;
      &.loading {
        background: @c0-b;
      }
    }
  }

  // State depending styles
  &.loading {
  }

  &.success {
    .state-label {
      color: @c9-g;
    }
    .progressinfo-pending {
      display: none;
    }
    .progressinfo-finished {
      display: inline-block;
    }
  }

  &.failed {
  }
}
</style>
